/**
 * SafeRow — per-row React error boundary for admin tables.
 *
 * Why this exists: one bad DB row (null where we expected a number,
 * deleted referenced doc, malformed JSON blob) used to crash the
 * entire admin page via the global error boundary → "500 Something
 * broke" screen with zero context, blocking every other valid row.
 *
 * With SafeRow, that single bad row degrades to a small red
 * "Couldn't render — <id>" cell while every other row keeps working.
 * Admin can identify and fix the offender without losing access to
 * the table.
 *
 * Renders inside a `<table>` `<tbody>` so the fallback MUST also be a
 * valid `<tr>` (using a different element breaks DOM validity and
 * hides the cell on some browsers).
 */
import React from "react";

export default class SafeRow extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface the failure in the console with the row id so ops can
    // find the offending record without grepping. Do NOT re-raise —
    // we want the rest of the table to keep rendering.
    console.error(
      `[SafeRow] row id=${this.props.rowId || "(unknown)"} failed to render:`,
      error, info?.componentStack,
    );
  }

  render() {
    if (this.state.error) {
      return (
        <tr
          className="bg-red-50"
          data-testid={`safe-row-error-${this.props.rowId || "unknown"}`}
        >
          <td
            colSpan={this.props.colSpan || 12}
            className="px-4 py-3 text-[12px] text-red-900"
          >
            <span className="font-mono font-bold mr-2">⚠ Render failed</span>
            <span className="text-[#9A2B2F]">
              Row <code className="bg-white border border-red-200 px-1 py-0.5 rounded">{this.props.rowId || "(unknown id)"}</code>{" "}
              couldn&apos;t be drawn — likely a bad/missing field in this record.
              The other rows are fine; check the browser console for the exact stack.
            </span>
          </td>
        </tr>
      );
    }
    return this.props.children;
  }
}
